import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ScrollPanel } from 'primeng/scrollpanel';
import { Subject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Room } from 'src/app/models/room.model';
import { Thread } from 'src/app/models/thread.model';
import { ClientcommunicationService } from 'src/app/services/clientcommunication/clientcommunication.service';
import { CommonFunctions } from 'src/app/Utilities/CommonFunctions';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-chat-room',
  templateUrl: './chat-room.component.html',
  styleUrls: ['./chat-room.component.css']
})
export class ChatRoomComponent implements OnInit {
  @ViewChild('sc', { static: false }) scroller?: ScrollPanel;
  scHeight: number = 0;
  thread: Thread = new Thread();
  room: Room = new Room();
  messageList: any[] = [];
  messageListRaw: any[] = [];
  threads: Thread[] = [];
  subs: Subscription[] = [];
  user_id: string = '';

  // Pagination state
  messagesPerPage = 10;
  isLoadingMore = false;
  hasMoreMessages = true;
  isLoading: boolean = false;

  private readonly destroy$ = new Subject<void>();
  getRoutePathParam: any;
  private currentRoomId: string | null = null;
  private currentBusinessId: string | null = null;
  private messageAddedSub?: Subscription;
  private typingSub?: Subscription;
  private messagesReadSub?: Subscription;
  private typingClearTimer: any;

  @Output() RoomData: EventEmitter<any> = new EventEmitter<any>();
  @Output() TypingChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    private clientcommunicationService: ClientcommunicationService,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    let user = CommonFunctions.getUser();
    this.user_id = user.id;
    this.retrieveThreads();
    this.scroll = this.scroll.bind(this);
    this.scroller?.contentViewChild.nativeElement.addEventListener('scroll', this.scroll);
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private mapConversationToRoom(conv: any): Room {
    return {
      id: conv.id,
      business_id: conv.businessId,
      business_name: conv.businessName,
      business_logo: conv.businessLogo,
      recepient_id: conv.recipientId,
      recepient_name: conv.recipientName,
      last_message: conv.lastMessage,
      preference_type: conv.preferenceType,
      read_status: conv.adminReadStatus,
      timestamp: conv.timestamp
    };
  }

  private mapMessage(m: any): any {
    return {
      docId: m.id,
      sender_id: m.senderId,
      sender_name: m.senderName,
      sender_image: m.senderImage,
      sender_type: m.senderType,
      message_channel_type: m.messageChannelType,
      text: m.text,
      attachments: m.attachments,
      read_status: m.readStatus,
      timestamp: m.timestamp
    };
  }

  retrieveThreads(): void {
    this.subs.push(
      this.route.paramMap
        .pipe(map((paramMap: any) => paramMap.get('id')))
        .subscribe(routePathParam => {
          this.getRoutePathParam = routePathParam;

          // Leave the previous room's socket channel so joined rooms don't stack.
          if (this.currentRoomId && this.currentBusinessId) {
            this.clientcommunicationService.leaveRoomChannel(this.currentBusinessId, this.currentRoomId);
          }
          this.messageAddedSub?.unsubscribe();
          this.typingSub?.unsubscribe();
          this.messagesReadSub?.unsubscribe();
          clearTimeout(this.typingClearTimer);
          this.TypingChanged.emit(false);

          // Reset pagination flags on room change
          this.messageListRaw = [];
          this.messageList = [];
          this.isLoadingMore = false;
          this.hasMoreMessages = true;
          this.currentRoomId = routePathParam;

          // Update the path param state
          this.clientcommunicationService.updatePathParamState(routePathParam);

          this.loadRoom(routePathParam);
          this.loadInitialMessages(routePathParam);
        })
    );
  }

  private loadRoom(roomId: string): void {
    this.clientcommunicationService.getSupportRoomRest(roomId).subscribe((response: any) => {
      const conv = response?.data;
      if (!conv) return;
      this.room = this.mapConversationToRoom(conv);
      this.currentBusinessId = conv.businessId;
      this.RoomData.emit(this.room);

      this.clientcommunicationService.connectChatSocket();
      this.clientcommunicationService.joinRoomChannel(conv.businessId, roomId);
      this.messageAddedSub = this.clientcommunicationService.onMessageAdded().subscribe((message: any) => {
        this.messageListRaw.push(this.mapMessage(message));
        this.messageFormat();
        this.scroller?.scrollTop(this.scHeight);
        this.showMessageSuccess();

        // The room is already open and being actively viewed — a live message from the business
        // counts as read right away. Without this, read-receipts only update the next time the
        // room is opened/reopened, not while both sides are chatting in real time.
        if (message.senderId !== '01') {
          this.clientcommunicationService.markSupportRoomReadRest(roomId).subscribe();
        }
      });

      this.typingSub = this.clientcommunicationService.onTyping().subscribe((data: any) => {
        if (data.senderType === 'System') {
          return; // our own broadcast, echoed back to us — ignore
        }
        this.TypingChanged.emit(data.isTyping);
        clearTimeout(this.typingClearTimer);
        if (data.isTyping) {
          // Safety net in case a "stopped typing" event is lost (e.g. a dropped connection).
          this.typingClearTimer = setTimeout(() => this.TypingChanged.emit(false), 5000);
        }
      });

      this.messagesReadSub = this.clientcommunicationService.onMessagesRead().subscribe((data: any) => {
        if (data.roomId !== roomId) {
          return;
        }
        let changed = false;
        this.messageListRaw.forEach((m: any) => {
          if (m.sender_id === data.senderId && m.read_status !== 'read') {
            m.read_status = 'read';
            changed = true;
          }
        });
        if (changed) {
          this.messageFormat();
        }
      });

      // '01' means the business hasn't seen the latest activity yet on the admin side.
      if (this.room.read_status == 'unread') {
        this.clientcommunicationService.markSupportRoomReadRest(roomId).subscribe(() => { });
      }
    });
  }

  private loadInitialMessages(roomId: string): void {
    this.isLoading = true;
    this.clientcommunicationService.getSupportMessagesRest(roomId, this.messagesPerPage).subscribe((response: any) => {
      const data = response?.data || [];
      const messages = data.map((m: any) => this.mapMessage(m)).reverse(); // oldest first
      this.messageListRaw = messages;
      this.hasMoreMessages = data.length === this.messagesPerPage;
      this.messageFormat();
      this.isLoading = false;

      this.scroller?.scrollTop(this.scHeight);
      this.showMessageSuccess();
    }, () => {
      this.isLoading = false;
    });
  }

  messageFormat() {
    let groupById: any[] = [];
    let prevId: any = '';
    let prevTimeTag: string = '';
    this.messageList = [];

    for (let index = 0; index < this.messageListRaw.length; index++) {
      let element: any = this.messageListRaw[index];

      let timeTag = this.getTimeAndDateTag(element.timestamp, 'chatroom');

      if (index === 0 || prevId !== element.sender_id || prevTimeTag !== timeTag) {
        groupById = [element];
        this.messageList.push({ id: element.sender_id, timeTag, subMsg: groupById });
      } else {
        groupById.push(element);
      }

      prevId = element.sender_id;
      prevTimeTag = timeTag;
    }
  }

  getTimeAndDateTag(timestamp: any, mode: 'chatroom' | 'grouping'): string {
    if (!timestamp) return '';

    let date: Date;
    try {
      if (typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();                              // Firestore Timestamp object
      } else if (timestamp.seconds && typeof timestamp.seconds === 'number') {
        date = new Date(timestamp.seconds * 1000);             // Firestore { seconds, nanoseconds }
      } else if (timestamp instanceof Date) {
        date = timestamp;                                       // Already a JS Date
      } else {
        date = new Date(timestamp);                            // ISO-8601 string from the Mongo REST API
      }

      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', timestamp);
        return '';
      }
    } catch (e) {
      console.warn('Error parsing timestamp:', timestamp, e);
      return '';
    }

    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const pad = (n: number) => n.toString().padStart(2, '0');
    const dateKey = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    const todayKey = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    const yesterdayKey = `${yesterday.getFullYear()}-${pad(yesterday.getMonth() + 1)}-${pad(yesterday.getDate())}`;

    let label: string;
    if (dateKey === todayKey) label = 'Today';
    else if (dateKey === yesterdayKey) label = 'Yesterday';
    else label = date.toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'short', day: 'numeric'
    });

    return mode === 'chatroom' ? label : dateKey;
  }

  showMessageSuccess() {
    var that = this;
    setTimeout(function () {
      that.scHeight = that.scroller?.contentViewChild.nativeElement.scrollHeight || 0;
      that.scroller?.scrollTop(that.scHeight);
    }, 1000);
  }

  scroll(e: Event) {
  }

  ngAfterViewInit(): void {
    const scrollElement = this.scroller?.contentViewChild?.nativeElement;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', this.handleScroll.bind(this));
    }
  }

  handleScroll(event: any): void {
    const scrollTop = event.target.scrollTop;
    if (scrollTop === 0 && this.hasMoreMessages && !this.isLoadingMore) {
      this.loadMoreMessages();
    }
  }

  loadMoreMessages(): void {
    if (this.messageListRaw.length === 0) return;

    this.isLoadingMore = true;
    this.isLoading = true;

    const oldestTimestamp = this.messageListRaw[0].timestamp;
    const before = new Date(oldestTimestamp).getTime();
    const roomId = this.route.snapshot.paramMap.get('id')!;

    this.clientcommunicationService.getSupportMessagesRest(roomId, this.messagesPerPage, before)
      .subscribe((response: any) => {
        this.isLoadingMore = false;
        this.isLoading = false;

        const data = response?.data || [];
        if (data.length === 0) {
          this.hasMoreMessages = false;
          return;
        }

        const older = data.map((m: any) => this.mapMessage(m)).reverse();
        this.hasMoreMessages = data.length === this.messagesPerPage;
        this.messageListRaw = [...older, ...this.messageListRaw]; // prepend older messages
        this.messageFormat();

        setTimeout(() => this.scroller?.scrollTop(50), 0);
      }, () => {
        this.isLoadingMore = false;
        this.isLoading = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.subs.forEach(sub => sub.unsubscribe());
    this.subs.length = 0;

    this.messageAddedSub?.unsubscribe();
    this.typingSub?.unsubscribe();
    this.messagesReadSub?.unsubscribe();
    clearTimeout(this.typingClearTimer);
    if (this.currentRoomId && this.currentBusinessId) {
      this.clientcommunicationService.leaveRoomChannel(this.currentBusinessId, this.currentRoomId);
    }

    this.scroller?.contentViewChild?.nativeElement?.removeEventListener('scroll', this.scroll);
  }
}
